import React, { useState, useEffect } from 'react';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import BookingModal from './components/BookingModal';
import AdminDashboard from './components/AdminDashboard';
import AIAssistant from './components/AIAssistant';
import { Calendar as CalendarIcon, LayoutDashboard } from 'lucide-react';

function App() {
  const [view, setView] = useState('calendar'); // 'calendar' or 'admin'
  const [bookings, setBookings] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const [editingBooking, setEditingBooking] = useState(null);

  // Load bookings from localStorage
  useEffect(() => {
    const saved = localStorage.getItem('mini-bookings');
    if (saved) setBookings(JSON.parse(saved));
  }, []);

  // Save bookings to localStorage
  useEffect(() => {
    localStorage.setItem('mini-bookings', JSON.stringify(bookings));
  }, [bookings]);

  const handleDateClick = (arg) => {
    setSelectedDate(arg);
    setEditingBooking(null);
    setIsModalOpen(true);
  };

  const handleEventClick = (clickInfo) => {
    setEditingBooking({
      id: clickInfo.event.id,
      title: clickInfo.event.title,
      start: clickInfo.event.startStr,
      end: clickInfo.event.endStr,
      extendedProps: clickInfo.event.extendedProps
    });
    setIsModalOpen(true);
  };

  const handleSaveBooking = (bookingData) => {
    if (editingBooking) {
      setBookings(bookings.map(b => b.id === bookingData.id ? bookingData : b));
    } else {
      setBookings([...bookings, { ...bookingData, extendedProps: { status: 'pending', ...bookingData } }]);
    }
    setIsModalOpen(false);
  };

  const handleDeleteBooking = (id) => {
    if (window.confirm('Are you sure you want to delete this booking?')) {
      setBookings(bookings.filter(b => b.id !== id));
    }
  };

  const handleStatusChange = (id, status) => {
    setBookings(bookings.map(b =>
      b.id === id ? { ...b, extendedProps: { ...b.extendedProps, status } } : b
    ));
  };

  const handleAISuggestion = (suggestion) => {
    setSelectedDate({ dateStr: suggestion.date });
    setIsModalOpen(true);
  };

  return (
    <div className="min-h-screen bg-dark-bg text-white p-6">
      <header className="max-w-7xl mx-auto mb-8 flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-poppins font-bold bg-gradient-primary bg-clip-text text-transparent">
            Smart Mini Booking
          </h1>
          <p className="text-white/60 text-sm">AI-Powered Scheduling System</p>
        </div>

        <div className="flex gap-4">
          <button
            onClick={() => setView('calendar')}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${view === 'calendar' ? 'bg-white/10 text-white' : 'text-white/60 hover:text-white'}`}
          >
            <CalendarIcon size={20} />
            Calendar
          </button>
          <button
            onClick={() => setView('admin')}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${view === 'admin' ? 'bg-white/10 text-white' : 'text-white/60 hover:text-white'}`}
          >
            <LayoutDashboard size={20} />
            Admin
          </button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-[1fr_350px] gap-8">
        {/* Main Content Area */}
        <div className="glass-card p-6 min-h-[600px]">
          {view === 'calendar' ? (
            <FullCalendar
              plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
              initialView="timeGridWeek"
              headerToolbar={{
                left: 'prev,next today',
                center: 'title',
                right: 'dayGridMonth,timeGridWeek,timeGridDay'
              }}
              events={bookings}
              dateClick={handleDateClick}
              eventClick={handleEventClick}
              height="auto"
              slotMinTime="08:00:00"
              slotMaxTime="20:00:00"
              allDaySlot={false}
              editable={true}
              selectable={true}
            />
          ) : (
            <AdminDashboard
              bookings={bookings}
              onDelete={handleDeleteBooking}
              onStatusChange={handleStatusChange}
            />
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          <AIAssistant onSuggest={handleAISuggestion} />

          <div className="glass-card p-6">
            <h3 className="font-semibold mb-4">Quick Stats</h3>
            <div className="space-y-3">
              <div className="flex justify-between text-sm">
                <span className="text-white/60">Total Bookings</span>
                <span>{bookings.length}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-white/60">Confirmed</span>
                <span className="text-green-400">
                  {bookings.filter(b => b.extendedProps?.status === 'confirmed').length}
                </span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-white/60">Pending</span>
                <span className="text-yellow-400">
                  {bookings.filter(b => b.extendedProps?.status === 'pending').length}
                </span>
              </div>
            </div>
          </div>
        </div>
      </main>

      <BookingModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSave={handleSaveBooking}
        selectedDate={selectedDate}
        existingBooking={editingBooking}
      />
    </div>
  );
}

export default App;
